import type { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { faInstagram } from "@fortawesome/free-brands-svg-icons/faInstagram";
import * as faIcons from "@fortawesome/pro-light-svg-icons";
import { ComponentType, SVGProps } from "react";
import * as Icons from "../icons/";

// Define a type that handles both string paths and Next.js StaticImageData
// depending upon the bundler being used (Vite + Trellis vs Next + Build Marketplace), the icon may be a string or an object
type IconAsset =
  | string
  | { src: string; height?: number; width?: number; blurDataURL?: string };

const faIconNames = [
  "angle-up",
  "angle-down",
  "arrow-left",
  "arrow-right",
  "bars",
  "book",
  "brush",
  "calculator",
  "calendar-xmark",
  "check",
  "chevron-circle-left",
  "chevron-circle-right",
  "circle",
  "circle-check",
  "circle-plus",
  "circle-user",
  "circle-question",
  "circle-minus",
  "circle-info",
  "circle-xmark",
  "clipboard-list",
  "envelope",
  "exclamation-triangle",
  "file-upload",
  "flag",
  "graduation-cap",
  "handshake",
  "heart",
  "hourglass",
  "house",
  "inbox",
  "instagram",
  "image",
  "leaf",
  "lightbulb",
  "lock",
  "newspaper",
  "pallet",
  "palette",
  "party-horn",
  "pencil",
  "phone",
  "question",
  "right-from-bracket",
  "search",
  "seedling",
  "shopping-bag",
  "shovel",
  "sparkles",
  "star",
  "stars",
  "stopwatch",
  "times",
  "trash-alt",
  "user",
  "xmark",
  "clouds-sun",
  "pen-ruler",
  "sun",
  "trees",
  "messages-dollar",
  "rabbit-running",
  "piggy-bank",
  "gauge",
  "gauge-max",
  "timeline-arrow",
  "building-wheat",
  "house-tree",
  "location-smile",
] as const;

export const svgIcons = {
  account: Icons.AccountIcon as string,
  "admin-read-convos": Icons.AdminReadConvosIcon as string,
  "already-has-contractor": Icons.AlreadyHasContractorIcon as string,
  avatar: Icons.AvatarIcon as string,
  backyard: Icons.BackyardIcon as string,
  bell: Icons.BellIcon as string,
  blueprint: Icons.BlueprintIcon as string,
  bonded: Icons.BondedIcon as string,
  budget: Icons.BudgetIcon as string,
  "build-project": Icons.BuildProjectIcon as string,
  calendar: Icons.CalendarIcon as string,
  "calendar-month": Icons.CalendarMonthIcon as string,
  chat2: Icons.Chat2Icon as string,
  chat3: Icons.ChatIcon3 as string,
  "chat-bubble": Icons.ChatBubble as string,
  chat: Icons.ChatIcon as string,
  "chat-outline": Icons.ChatOutlineIcon as string,
  check1: Icons.Check1Icon as string,
  checklist: Icons.ChecklistIcon as string,
  "circled-check": Icons.CircledCheckIcon as string,
  constructor: Icons.ConstructorIcon as string,
  conversation: Icons.ConversationIcon as string,
  copy: Icons.CopyIcon as string,
  "design-projects": Icons.DesignProjectsIcon as string,
  diy: Icons.DiyIcon as string,
  "eco-friendly": Icons.EcoFriendlyIcon as string,
  "estimate-match": Icons.EstimateMatchIcon as string,
  estimates: Icons.EstimatesIcon as string,
  "exterior-home": Icons.ExteriorHomeIcon as string,
  "family-owned": Icons.FamilyOwnedIcon as string,
  "female-founded": Icons.FemaleFoundedIcon as string,
  filters: Icons.FiltersIcon as string,
  "finish-quickly": Icons.FinishQuicklyIcon as string,
  "free-estimates": Icons.FreeEstimatesIcon as string,
  "front-yard": Icons.FrontYardIcon as string,
  "full-yard": Icons.FullYardIcon as string,
  goodbye: Icons.GoodbyeIcon as string,
  guidance: Icons.GuidanceIcon as string,
  "hand-shake": Icons.HandShakeIcon as string,
  "hanging-light": Icons.HangingLightIcon as string,
  helmet: Icons.HelmetIcon as string,
  "helmet-outline": Icons.HelmetOutlineIcon as string,
  "help-center": Icons.HelpCenterIcon as string,
  help: Icons.HelpIcon as string,
  "house-and-bushes": Icons.HouseAndBushes as string,
  licensed: Icons.LicensedIcon as string,
  "loading-arrows": Icons.LoadingArrows as string,
  "location-marker": Icons.LocationMarkerIcon as string,
  login: Icons.LoginIcon as string,
  logout: Icons.LogoutIcon as string,
  "measuement-tape": Icons.MeasuementTapeIcon as string,
  "meter-high": Icons.MeterHighIcon as string,
  "meter-low": Icons.MeterLowIcon as string,
  "no-image-available": Icons.NoImageAvailable as string,
  "owner-operator": Icons.OwnerOperatorIcon as string,
  phasing: Icons.PhasingIcon as string,
  phone: Icons.PhoneIcon as string,
  plan: Icons.PlanIcon as string,
  plant: Icons.PlantIcon as string,
  plus: Icons.PlusIcon as string,
  "premium-experience": Icons.PremiumExperienceIcon as string,
  "pro-promise-review": Icons.ProPromiseReviewIcon as string,
  "pro-promise-service": Icons.ProPromiseServiceIcon as string,
  "pro-promise-verify": Icons.ProPromiseVerifyIcon as string,
  pros: Icons.ProsIcon as string,
  "quality-contractor": Icons.QualityContractorIcon as string,
  "responsive-communication": Icons.ResponsiveCommunicationIcon as string,
  "super-pro": Icons.SuperProIcon as string,
  tap: Icons.TapIcon as string,
  "text-instruction-one": Icons.TextInstructionOneIcon as string,
  "text-instruction-three": Icons.TextInstructionThreeIcon as string,
  "text-instruction-two": Icons.TextInstructionTwoIcon as string,
  "traditional-2d-plans": Icons.Traditional2DPlansIcon as string,
  "traditional-confused": Icons.TraditionalConfusedIcon as string,
  "traditional-locked-in": Icons.TraditionalLockedInIcon as string,
  "traditional-time-consuming": Icons.TraditionalTimeConsumingIcon as string,
  "value-pro": Icons.ValueProIcon as string,
  "veteran-owned": Icons.VeteranOwnedIcon as string,
  "view-estimate": Icons.ViewEstimateIcon as string,
  "white-chat": Icons.WhiteChatIcon as string,
  wink: Icons.WinkIcon as string,
  yardzen: Icons.YardzenIcon as string,
  "yardzen-approved": Icons.YardzenApprovedIcon as string,
  "yardzen-contractor": Icons.YardzenContractorIcon as string,
  "yardzen-design-talent": Icons.YardzenDesignTalentIcon as string,
  "yardzen-logo-full": Icons.YardzenLogoFullIcon as string,
  "yardzen-logo-mobile": Icons.YardzenLogoMobileIcon as string,
  "yardzen-relaxed": Icons.YardzenRelaxedIcon as string,
  "yardzen-renders": Icons.YardzenRendersIcon as string,
  "years-in-business": Icons.YearsInBusinessIcon as string,

  adu: Icons.ProjectElementsIcons.aduIcon as string,

  "bbq-area": Icons.ProjectElementsIcons.bbqAreaIcon as string,
  deck: Icons.ProjectElementsIcons.deckIcon as string,
  "doors-replacement": Icons.ProjectElementsIcons
    .doorsReplacementIcon as string,
  driveway: Icons.ProjectElementsIcons.drivewayIcon as string,
  fences: Icons.ProjectElementsIcons.fencingIcon as string,
  "fire-pit": Icons.ProjectElementsIcons.firePitIcon as string,
  fountain: Icons.ProjectElementsIcons.fountainIcon as string,
  gates: Icons.ProjectElementsIcons.gatesIcon as string,
  "hot-tub": Icons.ProjectElementsIcons.hotTubIcon as string,
  "lawn-installation": Icons.ProjectElementsIcons
    .lawnInstallationIcon as string,
  "lawn-removal": Icons.ProjectElementsIcons.lawnRemovalIcon as string,
  "outdoor-kitchen": Icons.ProjectElementsIcons.outdoorKitchenIcon as string,
  "outdoor-lighting": Icons.ProjectElementsIcons.outdoorLightingIcon as string,
  "outdoor-play-area": Icons.ProjectElementsIcons.outdoorPlayAreaIcon as string,
  "outdoor-shower": Icons.ProjectElementsIcons.outdoorShowerIcon as string,
  painting: Icons.ProjectElementsIcons.paintingIcon as string,
  pathways: Icons.ProjectElementsIcons.pathwaysIcon as string,
  patio: Icons.ProjectElementsIcons.patioIcon as string,
  pergola: Icons.ProjectElementsIcons.pergolaIcon as string,
  planting: Icons.ProjectElementsIcons.plantingIcon as string,
  pool: Icons.ProjectElementsIcons.poolIcon as string,
  "raised-beds-vegetable-garden": Icons.ProjectElementsIcons
    .raisedBedsVegetableGardenIcon as string,
  "retaining-wall": Icons.ProjectElementsIcons.retainingWallIcon as string,
  roofing: Icons.ProjectElementsIcons.roofingIcon as string,
  shed: Icons.ProjectElementsIcons.shedIcon as string,
  "siding-replacement": Icons.ProjectElementsIcons
    .sidingReplacementIcon as string,
  "tree-removal": Icons.ProjectElementsIcons.treeRemovalIcon as string,
  trees: Icons.ProjectElementsIcons.treesIcon as string,
  "turf-installation": Icons.ProjectElementsIcons
    .turfInstallationIcon as string,
  "windows-replacement": Icons.ProjectElementsIcons
    .windowsReplacementIcon as string,

  "anything-else-1": Icons.MarketingIcons.LandscapeSolutionsAnythingElseIcon
    .default as string,
  "anything-else-2": Icons.MarketingIcons.LivingAreasAnythingElseIcon
    .default as string,
  "back-yard": Icons.MarketingIcons.ClassicPlansBackYardIcon.default as string,
  botanical: Icons.MarketingIcons.MarketingBotanical.default as string,
  "cold-plunge": Icons.MarketingIcons.LivingAreasColdPlungeIcon
    .default as string,
  "concierge-support": Icons.MarketingIcons.MarketingConciergeSupport
    .default as string,
  connect: Icons.MarketingIcons.MarketingConnect.default as string,
  "contractor-match": Icons.MarketingIcons.ClassicPlansContractorMatchIcon
    .default as string,
  "design-director": Icons.MarketingIcons.MarketingDesignDirector
    .default as string,
  dining: Icons.MarketingIcons.LivingAreasDining.default as string,
  "door-selection": Icons.MarketingIcons.MarketingDoorSelection
    .default as string,
  drainage: Icons.MarketingIcons.LandscapeSolutionsDrainageIcon
    .default as string,
  "edible-garden": Icons.MarketingIcons.LivingAreasEdibleGarden
    .default as string,
  "extra-revisions": Icons.MarketingIcons.MarketingExtraRevisions
    .default as string,
  "fast-delivery": Icons.MarketingIcons.MarketingFastDelivery.default as string,
  "furniture-and-decor": Icons.MarketingIcons.ClassicPlansFurnitureAndDecorIcon
    .default as string,
  "hardscaping-design": Icons.MarketingIcons.ClassicPlansHardscapingDesignIcon
    .default as string,
  "home-vision": Icons.MarketingIcons.MarketingHomeVision.default as string,
  "house-painting": Icons.MarketingIcons.MarketingHousePainting
    .default as string,
  ideas: Icons.MarketingIcons.MarketingIdeas.default as string,
  kids: Icons.MarketingIcons.LandscapeSolutionsKidsIcon.default as string,
  kitchen: Icons.MarketingIcons.LivingAreasKitchenIcon.default as string,
  "landscape-design": Icons.MarketingIcons.ClassicPlansLandscapeDesignIcon
    .default as string,
  "lighting-design": Icons.MarketingIcons.ClassicPlansLightingDesignIcon
    .default as string,
  lounge: Icons.MarketingIcons.LivingAreasLoungeIcon.default as string,
  "marketing-deck": Icons.MarketingIcons.LivingAreasDeckIcon.default as string,
  "marketing-firepit": Icons.MarketingIcons.LivingAreasFirepitIcon
    .default as string,
  "marketing-front-yard": Icons.MarketingIcons.ClassicPlansFrontYardIcon
    .default as string,
  "marketing-full-yard": Icons.MarketingIcons.ClassicPlansFullYardIcon
    .default as string,
  "marketing-patio": Icons.MarketingIcons.LivingAreasPatioIcon
    .default as string,
  "marketing-retaining-wall": Icons.MarketingIcons
    .LandscapeSolutionsRetainingWallIcon.default as string,
  "native-plants": Icons.MarketingIcons.LandscapeSolutionsNativePlantsIcon
    .default as string,
  noise: Icons.MarketingIcons.LandscapeSolutionsNoiseIcon.default as string,
  "one-designer-call": Icons.MarketingIcons.ClassicPlansOneDesignerCallIcon
    .default as string,
  "one-round-of-revisions": Icons.MarketingIcons
    .ClassicPlansOneRoundOfRevisionsIcon.default as string,
  "paint-color-selection": Icons.MarketingIcons.MarketingPaintColorSelection
    .default as string,
  pets: Icons.MarketingIcons.LandscapeSolutionsPetsIcon.default as string,
  "play-space": Icons.MarketingIcons.LivingAreasPlaySpaceIcon.default as string,
  privacy: Icons.MarketingIcons.LandscapeSolutionsPrivacyIcon.default as string,
  sauna: Icons.MarketingIcons.LivingAreasSaunaIcon.default as string,
  "seamless-process": Icons.MarketingIcons.ClassicPlansSeamlessProcessIcon
    .default as string,
  "seasonal-use": Icons.MarketingIcons.LandscapeSolutionsSeasonalUseIcon
    .default as string,
  shade: Icons.MarketingIcons.LandscapeSolutionsShadeIcon.default as string,
  "side-yard-design": Icons.MarketingIcons.MarketingSideYardDesign
    .default as string,
  slope: Icons.MarketingIcons.LandscapeSolutionsSlopeIcon.default as string,
  "swimming-pool": Icons.MarketingIcons.LivingAreasSwimmingPoolIcon
    .default as string,
  "water-reduction": Icons.MarketingIcons.LandscapeSolutionsWaterReductionIcon
    .default as string,
  "window-design": Icons.MarketingIcons.MarketingWindowDesign.default as string,
  "yard-functional": Icons.MarketingIcons.MarketingYardFunctional
    .default as string,

  "already-hired-builder": Icons.DesignQuizIcons
    .AlreadyHiredBuilderIcon as string,
  "backyard-house": Icons.DesignQuizIcons.BackyardHouseIcon as string,
  "beautiful-space": Icons.DesignQuizIcons.BeautifulSpaceIcon as string,
  cactus: Icons.DesignQuizIcons.CactusIcon as string,
  "calendar-alert": Icons.DesignQuizIcons.CalendarAlertIcon as string,
  "challenge-yard": Icons.DesignQuizIcons.ChallengeYardIcon as string,
  "design-quiz-cold-plunge": Icons.DesignQuizIcons.ColdPlungeIcon as string,
  "design-quiz-deck": Icons.DesignQuizIcons.DeckIcon as string,
  "design-quiz-dining": Icons.DesignQuizIcons.DiningIcon as string,
  "design-quiz-dream-deck": Icons.DesignQuizIcons.DreamDeckIcon as string,
  "design-quiz-dreaming": Icons.DesignQuizIcons.DreamingIcon as string,
  "exterior-redesign": Icons.DesignQuizIcons.ExteriorRedesignIcon as string,
  "exterior-surface": Icons.DesignQuizIcons.ExteriorSurfaceIcon as string,
  fix: Icons.DesignQuizIcons.FixIcon as string,
  "fix-hardscape": Icons.DesignQuizIcons.FixHardscapeIcon as string,
  "front-yard-house": Icons.DesignQuizIcons.FrontYardHouseIcon as string,
  "functional-yard": Icons.DesignQuizIcons.FunctionalYardIcon as string,
  "garage-door": Icons.DesignQuizIcons.GarageDoorIcon as string,
  garden: Icons.DesignQuizIcons.GardenIcon as string,
  guests: Icons.DesignQuizIcons.GuestsIcon as string,
  hardscaping: Icons.DesignQuizIcons.HardscapingIcon as string,
  hardware: Icons.DesignQuizIcons.HardwareIcon as string,
  "heritage-trees": Icons.DesignQuizIcons.HeritageTreesIcon as string,
  "design-quiz-hot-tub": Icons.DesignQuizIcons.HotTubIcon as string,
  "house-paint": Icons.DesignQuizIcons.HousePaintIcon as string,
  irrigation: Icons.DesignQuizIcons.IrrigationIcon as string,
  "design-quiz-kids": Icons.DesignQuizIcons.KidsIcon as string,
  "kinda-sloped": Icons.DesignQuizIcons.KindaSlopedIcon as string,
  "design-quiz-kitchen": Icons.DesignQuizIcons.KitchenIcon as string,
  "lacks-privacy": Icons.DesignQuizIcons.LacksPrivacyIcon as string,
  landscaping: Icons.DesignQuizIcons.LandscapingIcon as string,
  "life-into-date-yard": Icons.DesignQuizIcons.LifeIntoDateYardIcon as string,
  "light-challenge": Icons.DesignQuizIcons.LightChallengeIcon as string,
  lighting: Icons.DesignQuizIcons.LightingIcon as string,
  "little-slope": Icons.DesignQuizIcons.LittleSlopeIcon as string,
  "lot-of-trees": Icons.DesignQuizIcons.LotOfTreesIcon as string,
  myself: Icons.DesignQuizIcons.MyselfIcon as string,
  "new-space-yard": Icons.DesignQuizIcons.NewSpaceYardIcon as string,
  no: Icons.DesignQuizIcons.NoIcon as string,
  "design-quiz-patio": Icons.DesignQuizIcons.PatioIcon as string,
  "pet-dream-yard": Icons.DesignQuizIcons.PetDreamYardIcon as string,
  "pets-focus": Icons.DesignQuizIcons.PetsFocusIcon as string,
  "pets-no-focus": Icons.DesignQuizIcons.PetsNoFocusIcon as string,
  "pets-some-focus": Icons.DesignQuizIcons.PetsSomeFocusIcon as string,
  "design-quiz-pets": Icons.DesignQuizIcons.PetsIcon as string,
  "plain-terrain": Icons.DesignQuizIcons.PlainTerrainIcon as string,
  plants: Icons.DesignQuizIcons.PlantsIcon as string,
  playground: Icons.DesignQuizIcons.PlaygroundIcon as string,
  "design-quiz-privacy": Icons.DesignQuizIcons.PrivacyIcon as string,
  "ready-now": Icons.DesignQuizIcons.ReadyNowIcon as string,
  "ready-soon": Icons.DesignQuizIcons.ReadySoonIcon as string,
  "retaining-walls": Icons.DesignQuizIcons.RetainingWallsIcon as string,
  revive: Icons.DesignQuizIcons.ReviveIcon as string,
  "road-noise": Icons.DesignQuizIcons.RoadNoiseIcon as string,
  "design-quiz-sauna": Icons.DesignQuizIcons.SaunaIcon as string,
  "seasonal-interest": Icons.DesignQuizIcons.SeasonalInterestIcon as string,
  "side-yard": Icons.DesignQuizIcons.SideYardIcon as string,
  sloped: Icons.DesignQuizIcons.SlopedIcon as string,
  spring: Icons.DesignQuizIcons.SpringIcon as string,
  style: Icons.DesignQuizIcons.StyleIcon as string,
  "design-quiz-swimming-pool": Icons.DesignQuizIcons.SwimmingPoolIcon as string,
  "very-sloped": Icons.DesignQuizIcons.VerySlopedIcon as string,
  "water-consumption": Icons.DesignQuizIcons.WaterConsumptionIcon as string,
  "whole-yard": Icons.DesignQuizIcons.WholeYardIcon as string,
  "with-trees": Icons.DesignQuizIcons.WithTreesIcon as string,
  "yard-maintenance": Icons.DesignQuizIcons.YardMaintenanceIcon as string,
  yes: Icons.DesignQuizIcons.YesIcon as string,
  "new-build": Icons.DesignQuizIcons.NewBuildIcon as string,
  "from-scratch": Icons.DesignQuizIcons.FromScratchIcon as string,
  "water-drainage": Icons.DesignQuizIcons.WaterDrainageIcon as string,
  "failing-infra": Icons.DesignQuizIcons.FailingInfraIcon as string,
  "slope-challenge": Icons.DesignQuizIcons.SlopeChallengeIcon as string,
  "something-else-challenge": Icons.DesignQuizIcons
    .SomethingElseChallengeIcon as string,
  entertaining: Icons.DesignQuizIcons.EntertainingIcon as string,
  playing: Icons.DesignQuizIcons.PlayingIcon as string,
  cooking: Icons.DesignQuizIcons.CookingIcon as string,
  relaxing: Icons.DesignQuizIcons.RelaxingIcon as string,
  wellness: Icons.DesignQuizIcons.WellnessIcon as string,
  "enjoy-view": Icons.DesignQuizIcons.EnjoyViewIcon as string,
  "design-quiz-calendar": Icons.DesignQuizIcons.CalendarIcon as string,
  yesterday: Icons.DesignQuizIcons.YesterdayIcon as string,
  "doors-windows": Icons.DesignQuizIcons.DoorWindowIcon as string,
  "location-pin": Icons.DesignQuizIcons.LocationIcon as string,
  "yard-onboarding": Icons.OnboardingWishlistIcons.yardOnboarding as string,
  "yard-area": Icons.DesignQuizIcons.YardAreaIcon as string,
  "design-quiz-pathways": Icons.DesignQuizIcons.PathwaysIcon as string,
  "design-quiz-almost-done": Icons.DesignQuizIcons.QuizAlmostDoneIcon as string,
  "design-quiz-dream-yard": Icons.DesignQuizIcons.QuizDreamYard as string,
  "design-quiz-start": Icons.DesignQuizIcons.QuizStartIcon as string,
  "design-quiz-tell-us": Icons.DesignQuizIcons.QuizTellUsIcon as string,
} as const;

export const reactIcons = {
  "chevron-right": Icons.ArrowRight,
  calendar: Icons.Calendar,
  "chat-filled": Icons.ChatFilled,
  "circled-check": Icons.CircledCheck,
  "new-tab-link": Icons.NewTabLink,
  "paper-airplane": Icons.PaperAirplane,
  sweat: Icons.Sweat,
  "tooltip-info": Icons.TooltipInfo,
} as const;

export type SvgComponentType = IconAsset;
export type ReactComponentType = ComponentType<{
  className?: string;
  [key: string]: any;
}>;

export const pngIcons = {
  "affirm-logo": Icons.AffirmLogoIcon as IconAsset,
  "yz-pro-header-logo": Icons.YzProHeaderLogoIcon as IconAsset,
  "fountain-large": Icons.OnboardingWishlistIcons.fountainLarge,
  "fountain-medium": Icons.OnboardingWishlistIcons.fountainMedium,
  "fountain-small": Icons.OnboardingWishlistIcons.fountainSmall,
  "outdoor-shower-large": Icons.OnboardingWishlistIcons.outdoorShowerLarge,
  "outdoor-shower-medium": Icons.OnboardingWishlistIcons.outdoorShowerMedium,
  "outdoor-shower-small": Icons.OnboardingWishlistIcons.outdoorShowerSmall,
  "hot-tub-large": Icons.OnboardingWishlistIcons.hotTubLarge,
  "hot-tub-medium": Icons.OnboardingWishlistIcons.hotTubMedium,
  "hot-tub-small": Icons.OnboardingWishlistIcons.hotTubSmall,
  "pool-large": Icons.OnboardingWishlistIcons.poolLarge,
  "pool-medium": Icons.OnboardingWishlistIcons.poolMedium,
  "pool-small": Icons.OnboardingWishlistIcons.poolSmall,
  "staircase-large": Icons.OnboardingWishlistIcons.staircaseLarge,
  "staircase-medium": Icons.OnboardingWishlistIcons.staircaseMedium,
  "staircase-small": Icons.OnboardingWishlistIcons.staircaseSmall,
  "railings-large": Icons.OnboardingWishlistIcons.railingsLarge,
  "railings-medium": Icons.OnboardingWishlistIcons.railingsMedium,
  "railings-small": Icons.OnboardingWishlistIcons.railingsSmall,
  "driveway-gate-automatic":
    Icons.OnboardingWishlistIcons.drivewayGateAutomatic,
  "driveway-gate-sliding": Icons.OnboardingWishlistIcons.drivewayGateSliding,
  "driveway-gate-swing": Icons.OnboardingWishlistIcons.drivewayGateSwing,
  "outdoor-lighting-large": Icons.OnboardingWishlistIcons.outdoorLightingLarge,
  "outdoor-lighting-medium":
    Icons.OnboardingWishlistIcons.outdoorLightingMedium,
  "outdoor-lighting-small": Icons.OnboardingWishlistIcons.outdoorLightingSmall,
  "retaining-wall-large": Icons.OnboardingWishlistIcons.retainingWallLarge,
  "retaining-wall-medium": Icons.OnboardingWishlistIcons.retainingWallMedium,
  "retaining-wall-small": Icons.OnboardingWishlistIcons.retainingWallSmall,
  "tall-fencing-large": Icons.OnboardingWishlistIcons.tallFencingLarge,
  "tall-fencing-medium": Icons.OnboardingWishlistIcons.tallFencingMedium,
  "tall-fencing-small": Icons.OnboardingWishlistIcons.tallFencingSmall,
  "short-fencing-large": Icons.OnboardingWishlistIcons.shortFencingLarge,
  "short-fencing-medium": Icons.OnboardingWishlistIcons.shortFencingMedium,
  "short-fencing-small": Icons.OnboardingWishlistIcons.shortFencingSmall,
  "play-structure-large": Icons.OnboardingWishlistIcons.playStructureLarge,
  "play-structure-medium": Icons.OnboardingWishlistIcons.playStructureMedium,
  "play-structure-small": Icons.OnboardingWishlistIcons.playStructureSmall,
  "deck-large": Icons.OnboardingWishlistIcons.deckLarge,
  "deck-medium": Icons.OnboardingWishlistIcons.deckMedium,
  "deck-small": Icons.OnboardingWishlistIcons.deckSmall,
  "driveway-large": Icons.OnboardingWishlistIcons.drivewayLarge,
  "driveway-medium": Icons.OnboardingWishlistIcons.drivewayMedium,
  "driveway-small": Icons.OnboardingWishlistIcons.drivewaySmall,
  "paved-patio-medium-large":
    Icons.OnboardingWishlistIcons.pavedPatioMediumLarge,
  "paved-patio-small": Icons.OnboardingWishlistIcons.pavedPatioSmall,
  "gravel-patio-medium-large":
    Icons.OnboardingWishlistIcons.gravelPatioMediumLarge,
  "gravel-patio-small": Icons.OnboardingWishlistIcons.gravelPatioSmall,
  "pathway-large": Icons.OnboardingWishlistIcons.pathwayLarge,
  "pathway-medium": Icons.OnboardingWishlistIcons.pathwayMedium,
  "pathway-small": Icons.OnboardingWishlistIcons.pathwaySmall,
  "grill-large": Icons.OnboardingWishlistIcons.grillLarge,
  "grill-medium": Icons.OnboardingWishlistIcons.grillMedium,
  "grill-small": Icons.OnboardingWishlistIcons.grillSmall,
  "pergola-medium-large": Icons.OnboardingWishlistIcons.pergolaMediumLarge,
  "pergola-small": Icons.OnboardingWishlistIcons.pergolaSmall,
  "outdoor-kitchen-large": Icons.OnboardingWishlistIcons.outdoorKitchenLarge,
  "outdoor-kitchen-small-medium":
    Icons.OnboardingWishlistIcons.outdoorKitchenSmallMedium,
  "firepit-large": Icons.OnboardingWishlistIcons.firepitLarge,
  "firepit-medium": Icons.OnboardingWishlistIcons.firepitMedium,
  "firepit-small": Icons.OnboardingWishlistIcons.firepitSmall,
  "planting-dense": Icons.OnboardingWishlistIcons.plantingDense,
  "planting-moderate": Icons.OnboardingWishlistIcons.plantingModerate,
  "planting-sparse": Icons.OnboardingWishlistIcons.plantingSparse,
  "raised-beds-large": Icons.OnboardingWishlistIcons.raisedBedsLarge,
  "raised-beds-medium": Icons.OnboardingWishlistIcons.raisedBedsMedium,
  "raised-beds-small": Icons.OnboardingWishlistIcons.raisedBedsSmall,
  "lawn-large": Icons.OnboardingWishlistIcons.lawnLarge,
  "lawn-medium": Icons.OnboardingWishlistIcons.lawnMedium,
  "lawn-small": Icons.OnboardingWishlistIcons.lawnSmall,
  "trees-small": Icons.OnboardingWishlistIcons.treesSmall,
  "trees-medium": Icons.OnboardingWishlistIcons.treesMedium,
  "trees-large": Icons.OnboardingWishlistIcons.treesLarge,
  "stairs-small": Icons.OnboardingWishlistIcons.stairsSmall,
  "stairs-medium": Icons.OnboardingWishlistIcons.stairsMedium,
  "stairs-large": Icons.OnboardingWishlistIcons.stairsLarge,
} as const;

type FaIconName = (typeof faIconNames)[number];
type SvgIconName = keyof typeof svgIcons;
type ReactIconName = keyof typeof reactIcons;
type PngIconName = keyof typeof pngIcons;

export type IconName = FaIconName | SvgIconName | ReactIconName | PngIconName;

export const icons = {
  collections: {
    fa: faIconNames,
    svg: svgIcons,
    react: reactIcons,
    png: pngIcons,
  },

  isFa: (name: string): name is FaIconName =>
    faIconNames.includes(name as FaIconName),

  isSvg: (name: string): name is SvgIconName => name in svgIcons,

  isReact: (name: string): name is ReactIconName => name in reactIcons,

  isPng: (name: string): name is PngIconName => name in pngIcons,

  isValid: (name: string): name is IconName =>
    icons.isFa(name) ||
    icons.isSvg(name) ||
    icons.isReact(name) ||
    icons.isPng(name),

  getFa: (name: FaIconName) => {
    if (name === "instagram") {
      return faInstagram;
    }

    return faIcons[
      `fa${name
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join("")}` as keyof typeof faIcons
    ] as IconDefinition;
  },

  getSvg: (name: SvgIconName): SvgComponentType => svgIcons[name],

  getReact: (name: ReactIconName) => reactIcons[name],

  getPng: (name: PngIconName): SvgComponentType => pngIcons[name],

  isSvgComponent: (
    component: unknown,
  ): component is ComponentType<SVGProps<SVGSVGElement>> =>
    typeof component === "function",
};

// Export availableIcons (all icon names from collections)
export const availableIcons = [
  ...icons.collections.fa,
  ...(Object.keys(icons.collections.svg) as SvgIconName[]),
  ...(Object.keys(icons.collections.react) as ReactIconName[]),
  ...(Object.keys(icons.collections.png) as PngIconName[]),
];
